import React, { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";

import { request } from "hooks/useOkoApi/useOkoApi";

const ENVIRONMENTS = ["UAT", "PROD"];

/**
 * Tab 2 — Manual Sync. The button calls the superuser
 * POST /integrations/ednition/sync, which QUEUES the sync as a background task
 * and returns 202 immediately (the run can outlast the gateway timeout).
 *
 * The optional owner filter scopes the run to a single credential owner. A
 * credential is keyed by EITHER a school (school_id) OR a client (client_id),
 * so the owner picker mirrors that XOR: pick a kind, then the owner. Leaving it
 * on "All owners" runs every in-scope credential for the environment (the cron
 * form). The final report is written to the server logs; the UI confirms the
 * run started.
 */
export const ManualSyncTab = () => {
  const [environment, setEnvironment] = useState("UAT");
  const [ownerKind, setOwnerKind] = useState("school");
  const [schools, setSchools] = useState([]);
  const [clients, setClients] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [accepted, setAccepted] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [schoolData, clientData] = await Promise.all([
          request("admin/schools", { method: "GET" }),
          request("admin/clients", { method: "GET" }),
        ]);
        setSchools(schoolData?.schools || []);
        setClients(clientData?.clients || []);
      } catch (e) {
        setError(e?.detail || "Failed to load schools/clients");
      }
    })();
  }, []);

  const owners = ownerKind === "school" ? schools : clients;

  const runSync = async () => {
    setError(null);
    setAccepted(null);
    setRunning(true);
    const query = new URLSearchParams({ environment });
    if (ownerId) {
      query.set(ownerKind === "school" ? "school_id" : "client_id", ownerId);
    }
    try {
      const data = await request(
        `integrations/ednition/sync?${query.toString()}`,
        { method: "POST" }
      );
      setAccepted(data);
    } catch (e) {
      setError(e?.detail || "Sync failed");
    } finally {
      setRunning(false);
    }
  };

  const scopeLabel = ownerId
    ? owners.find((o) => o.id === ownerId)?.name || "the selected owner"
    : "all owners";

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form.Group controlId="syncEnvironment">
        <Form.Label>Environment</Form.Label>
        <Form.Control
          as="select"
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
        >
          {ENVIRONMENTS.map((env) => (
            <option key={env} value={env}>{env}</option>
          ))}
        </Form.Control>
      </Form.Group>

      <Form.Group controlId="syncOwnerKind" style={{ marginTop: "12px" }}>
        <Form.Label>Owner kind</Form.Label>
        <Form.Control
          as="select"
          value={ownerKind}
          onChange={(e) => {
            setOwnerKind(e.target.value);
            setOwnerId("");
          }}
        >
          <option value="school">School-keyed</option>
          <option value="client">Client-keyed</option>
        </Form.Control>
      </Form.Group>

      <Form.Group controlId="syncOwner" style={{ marginTop: "12px" }}>
        <Form.Label>{ownerKind === "school" ? "School" : "Client"}</Form.Label>
        <Form.Control
          as="select"
          data-testid="sync-owner-select"
          value={ownerId}
          onChange={(e) => setOwnerId(e.target.value)}
        >
          <option value="">
            All {ownerKind === "school" ? "schools" : "clients"} (whole environment)
          </option>
          {owners.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </Form.Control>
      </Form.Group>

      <Button
        onClick={runSync}
        disabled={running}
        style={{ marginTop: "12px" }}
      >
        {running ? "Starting…" : "Run sync"}
      </Button>

      {accepted && (
        <Alert variant="success" style={{ marginTop: "24px" }}>
          <strong>
            Sync started for {accepted.environment} ({scopeLabel}).
          </strong>
          <div>
            {accepted.detail ||
              "The sync is running in the background. Check the server logs " +
                "for the report, then refresh View Entities to see the results."}
          </div>
        </Alert>
      )}
    </div>
  );
};
